import type paper from "paper";
import type { Project } from "./Project";
import { deriveAllConnections } from "./connections";
import {
  deriveRoundedCorners,
  reconcileRoundedPrimitives,
} from "./constructionRounding";
import { deriveRoundedLeatherCorners } from "./pieceRounding";
import { deriveFreehandSmoothing } from "./freehandSmoothing";
import { deriveLeatherSmoothing } from "./leatherSmoothing";
import { deriveConstructionLineKinds } from "./constructionKind";
import {
  deriveConstructionOutlines,
  bakeAllConstructionOutlines,
} from "./constructionOutline";
import { deriveConstructionStitchGrid } from "./constructionStitchGrid";
import { deriveStitchMatches } from "./stitchMatch";
import { isFeatureEnabled } from "../featureFlags";

/** Re-derive every piece of derived data in a project: smoothing, rounding,
 *  connections, construction outlines, the stitch grid and stitch matches.
 *
 *  The one derive chain, shared by `ctx.commit` and every path that loads a
 *  project (Open, autosave restore, reset — #1141), so a pass can't be added
 *  to one and forgotten in the other. Every pass is memoized: a project that
 *  is already current comes back as the same object. */
export function deriveProject(next: Project, scope: paper.PaperScope): Project {
  // Re-derive freehand-smoothing first so the rounded-corners and
  // offsets passes below read the already-smoothed pathData
  // (issue #171). Same memoization contract: returns the input
  // unchanged when no freehand line needs an update.
  const smoothed = deriveFreehandSmoothing(next, scope);
  // Same pass for freehand LEATHER geometry (#1016): a part, cutout or seam
  // carrying `rawPoints` re-derives its visible geometry at its current
  // smoothness. Before the rounding pass, for the same reason construction's
  // runs first — a rounded corner should follow the smoothed outline.
  const leatherSmoothed = deriveLeatherSmoothing(smoothed, scope);
  // Re-derive rounded-corner pathData (issue #100) so the offset pass below
  // reads the already-rounded source — an offset that follows a rounded
  // rectangle should track the rounded outline, not the sharp one.
  const rounded = deriveRoundedCorners(leatherSmoothed, scope);
  // Keep a rounded rect / ellipse's primitive box on the shape it draws.
  // Every geometry edit now writes the sharp source too (#1165), so on a
  // commit this is a no-op; it exists to repair projects saved while the
  // two could drift — and since Open and autosave restore both run here,
  // those files are fixed the moment they load.
  const reconciled = reconcileRoundedPrimitives(rounded, scope);
  // Same pass for leather outlines (#974): a part / cutout carrying
  // `cornerRounding` re-derives its visible outline from `sharpPathData`,
  // which is what keeps a corner radius adjustable and reversible. Same
  // memoization contract — the project comes back unchanged when nothing
  // is rounded.
  const leatherRounded = deriveRoundedLeatherCorners(reconciled, scope);
  // Run every connection kind's commit-time derive (issue #180
  // unification). Order matters: snap pruning → offset →
  // mirror, so mirror-of-offset reads the offset's freshly-
  // derived pathData on the same commit. Cycle detection runs
  // first per kind so a self-referential mirror/offset doesn't
  // loop the deriver. Adding a fourth kind is one new entry in
  // the registry — no change needed here.
  const connDerived = deriveAllConnections(leatherRounded, scope);
  // Recompute every non-primitive construction line's `kind` from its
  // current (smoothed / rounded / connection-derived) pathData so the panel
  // label reflects what the user actually sees, and live-rename auto-named
  // lines into the correct modifier bucket ("Polyline N" → "Polygon N" when
  // closed, etc.).
  const kinded = deriveConstructionLineKinds(connDerived);
  // Re-derive magnetic outlines (#37): a piece/cutout/stitch whose outline is
  // traced from construction segments rebuilds its geometry from the
  // now-final construction-line geometry. No-op when no entity has a
  // `constructionOutline`. Construction connections are a PAID feature
  // (#740): when the flag is off, keep NO construction↔shape relation — bake
  // every linked host to standalone geometry instead.
  const outlined = isFeatureEnabled("construction-connections")
    ? deriveConstructionOutlines(kinded, scope)
    : bakeAllConstructionOutlines(kinded);
  // Construction-anchored stitch grid (#199 Phase 3): a seam traced from a
  // construction line snaps its holes to that line's grid, so two seams on
  // the same line auto-align where they overlap. Gated with the rest of #199
  // (and needs the construction link); no-op otherwise.
  const gridded =
    isFeatureEnabled("stitch-matching") &&
    isFeatureEnabled("construction-connections")
      ? deriveConstructionStitchGrid(outlined, scope)
      : outlined;
  // After seam geometry is final, re-stamp matched stitch lines (#199) so
  // a matched pair shares its hole count and prunes when a member is gone.
  return deriveStitchMatches(gridded);
}
